import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/components/ui/use-toast';
import { CheckCircle, Clock, AlertTriangle, ExternalLink, RefreshCw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface ConnectAccountStatusProps {
  userId: string;
  accountId?: string;
  onStatusUpdate?: () => void;
}

interface AccountStatus {
  id: string;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  requirements: {
    currently_due: string[];
    eventually_due: string[];
    past_due: string[];
    pending_verification: string[];
    disabled_reason?: string;
  };
  details_submitted: boolean;
}

export function ConnectAccountStatus({ userId, accountId, onStatusUpdate }: ConnectAccountStatusProps) {
  const [account, setAccount] = useState<AccountStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { toast } = useToast();

  const fetchAccountStatus = async () => {
    try {
      // Get the current session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        console.error('No active session');
        setAccount(null);
        return;
      }

      // Call Supabase Edge Function to get account status
      const { data, error } = await supabase.functions.invoke('stripe-connect-status', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        console.error('Error fetching account status:', error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to fetch account status.",
        });
        setAccount(null);
      } else {
        setAccount(data);
        // Call onStatusUpdate callback when data is fetched
        if (onStatusUpdate) {
          onStatusUpdate();
        }
      }
    } catch (error) {
      console.error('Error fetching account status:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch account status.",
      });
      setAccount(null);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAccountStatus();
  }, [userId]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchAccountStatus();
  };

  const getAccountStatus = () => {
    if (!account) return { status: 'not_connected', progress: 0, color: 'secondary' as const };
    
    if (!account.details_submitted) {
      return { status: 'incomplete', progress: 25, color: 'warning' as const };
    }
    
    if (account.requirements.currently_due.length > 0 || account.requirements.past_due.length > 0) {
      return { status: 'requires_action', progress: 50, color: 'destructive' as const };
    }
    
    if (account.requirements.pending_verification.length > 0) {
      return { status: 'pending_verification', progress: 75, color: 'warning' as const };
    }
    
    if (account.chargesEnabled && account.payoutsEnabled) {
      return { status: 'active', progress: 100, color: 'success' as const };
    }
    
    return { status: 'restricted', progress: 60, color: 'warning' as const };
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'pending_verification':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'requires_action':
      case 'restricted':
        return <AlertTriangle className="h-5 w-5 text-red-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusMessage = (status: string) => {
    switch (status) {
      case 'not_connected':
        return 'Account not connected to Stripe';
      case 'incomplete':
        return 'Account setup incomplete';
      case 'requires_action':
        return 'Action required to complete verification';
      case 'pending_verification':
        return 'Verification documents under review';
      case 'restricted':
        return 'Account has restrictions';
      case 'active':
        return 'Account fully verified and active';
      default:
        return 'Unknown status';
    }
  };

  const createAccountLink = async (type: 'account_onboarding' | 'account_update') => {
    try {
      setIsLoading(true);
      
      // Get the current session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        toast({
          variant: "destructive",
          title: "Authentication Error",
          description: "Please sign in to continue.",
        });
        return;
      }

      // Call Supabase Edge Function to create account link
      const { data, error } = await supabase.functions.invoke('stripe-connect-link', {
        body: {
          type,
          accountId: accountId || account?.id
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        throw error;
      }

      if (!data?.accountLink) {
        throw new Error('No account link returned from server');
      }
      
      window.location.href = data.accountLink;
    } catch (error) {
      console.error('Error creating account link:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to open Stripe dashboard. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading && !account) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Stripe Account Status</CardTitle>
          <CardDescription>Loading account information...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const { status, progress, color } = getAccountStatus();

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              {getStatusIcon(status)}
              Stripe Account Status
            </CardTitle>
            <CardDescription>
              {getStatusMessage(status)}
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Verification Progress</span>
            <span className="text-sm text-muted-foreground">{progress}%</span>
          </div>
          <Progress value={progress} className="w-full" />
        </div>

        {account && (
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Capabilities</h4>
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Charges</span>
                  <Badge variant={account.chargesEnabled ? "default" : "secondary"}>
                    {account.chargesEnabled ? "Enabled" : "Disabled"}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Payouts</span>
                  <Badge variant={account.payoutsEnabled ? "default" : "secondary"}>
                    {account.payoutsEnabled ? "Enabled" : "Disabled"}
                  </Badge>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="text-sm font-medium">Requirements</h4>
              <div className="space-y-1 text-sm">
                {account.requirements.currently_due.length > 0 && (
                  <div className="text-red-600">
                    {account.requirements.currently_due.length} items due now
                  </div>
                )}
                {account.requirements.pending_verification.length > 0 && (
                  <div className="text-yellow-600">
                    {account.requirements.pending_verification.length} pending verification
                  </div>
                )}
                {account.requirements.eventually_due.length > 0 && (
                  <div className="text-gray-600">
                    {account.requirements.eventually_due.length} items due later
                  </div>
                )}
                {account.requirements.currently_due.length === 0 && 
                 account.requirements.pending_verification.length === 0 && 
                 account.requirements.eventually_due.length === 0 && (
                  <div className="text-green-600">All requirements met</div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-2 pt-4">
          {!account ? (
            <Button onClick={() => createAccountLink('account_onboarding')}>
              Connect Stripe Account
            </Button>
          ) : (
            <Button 
              variant="outline" 
              onClick={() => createAccountLink('account_update')}
              className="flex items-center gap-2"
            >
              <ExternalLink className="h-4 w-4" />
              Manage Account
            </Button>
          )}
        </div>

        {account?.requirements.disabled_reason && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3">
            <h4 className="text-sm font-medium text-red-800">Account Disabled</h4>
            <p className="text-sm text-red-700 mt-1">
              {account.requirements.disabled_reason}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
} 